import { auth } from "./firebase";

// Single API endpoint discovery. Production uses the deployed `api` Cloud
// Function URL (set via VITE_API_BASE_URL); dev defaults to the emulator.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.VITE_USE_EMULATORS === "true"
    ? "http://localhost:5001/oneprompteu/europe-west1/api"
    : "https://europe-west1-oneprompteu.cloudfunctions.net/api");

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth?.currentUser) {
    const token = await auth.currentUser.getIdToken();
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
    credentials: "omit",
  });
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const err = json as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiError(
      err?.error?.code ?? "internal",
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
      err?.error?.details,
    );
  }
  return json as T;
}

export const api = {
  get: <T = unknown>(path: string) => request<T>("GET", path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>("POST", path, body),
  patch: <T = unknown>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  delete: <T = unknown>(path: string) => request<T>("DELETE", path),
};
